import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Trophy, Star, Users, Gift } from 'lucide-react';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Payment verification timeout. Please contact support.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${REACT_APP_BACKEND_URL}/api/checkout/status/${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPollingAttempts(attempts + 1);

      if (data.payment_status === 'paid') {
        setPaymentData(data);
        setLoading(false);
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Error verifying payment. Please contact support.');
      setLoading(false);
    }
  };

  const generateSuccessMessage = () => {
    if (!paymentData) return '';

    const { entry_number, total_subscribers } = paymentData;

    // First 1,000 subscribers - eligible for sweepstakes
    if (total_subscribers <= 1000) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-green-100 border-2 border-green-400 rounded-lg">
            <Trophy className="w-8 h-8 text-green-600" />
            <div>
              <p className="font-bold text-lg text-green-800">🎉 You're In the Early Founder Sweepstakes!</p>
              <p className="text-green-700">You're one of the first 1,000 FltrLite Pro subscribers!</p>
              <p className="text-green-700">You've been automatically entered to win the <strong>$5,000 Cash Prize</strong>!</p>
            </div>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Note:</strong> The $5,000 prize will be awarded once we reach 1,000 total FltrLite Pro subscribers. 
              One winner will be randomly selected from all eligible entries.
            </p>
          </div>
        </div>
      );
    }

    // After 1,000 - not eligible for sweepstakes but still get benefits
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-blue-100 border-2 border-blue-400 rounded-lg">
          <Star className="w-8 h-8 text-blue-600" />
          <div>
            <p className="font-bold text-lg text-blue-800">🌟 Welcome to FltrLite Pro!</p>
            <p className="text-blue-700">Thank you for subscribing! While the Early Founder Sweepstakes was limited to the first 1,000 subscribers, you still have access to all the amazing FltrLite Pro features!</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-700">Verifying your payment...</p>
          <p className="text-sm text-gray-500 mt-2">Attempt {pollingAttempts + 1} of 10</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Payment Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">🎉 Thank You for Subscribing!</h1>
          <p className="text-xl text-gray-600">Welcome to FltrLite Pro!</p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-blue-600 mb-6">
          {/* Promo Code Section */}
          <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-xl p-6 mb-6 text-center">
            <Gift className="w-12 h-12 mx-auto mb-3 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Exclusive Promo Code</h2>
            <div className="bg-white rounded-lg py-4 px-6 inline-block border-4 border-blue-600">
              <p className="text-4xl font-bold text-blue-600 tracking-wider">{paymentData?.promo_code}</p>
            </div>
            <p className="text-sm text-gray-700 mt-3">We've sent this code to your email as well.</p>
            <p className="text-sm text-gray-600 mt-2">Enter this promo code in the Promo Code section of FltrHub for your profile to become FltrLite Pro subscribed!</p>
          </div>

          {/* Entry Number */}
          <div className="text-center mb-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-lg font-semibold text-gray-700">📊 Your Entry Number</p>
            <p className="text-3xl font-bold text-blue-600">#{paymentData?.entry_number}</p>
          </div>

          {/* Dynamic Message */}
          {generateSuccessMessage()}

          {/* FltrLite Pro Benefits */}
          <div className="mt-6 p-6 bg-gradient-to-r from-blue-100 to-green-100 rounded-xl border-2 border-blue-400">
            <h3 className="text-xl font-bold text-blue-800 mb-3 flex items-center gap-2">
              <Star className="w-6 h-6" />
              Your FltrLite Pro Benefits
            </h3>
            <ul className="space-y-2 text-blue-700">
              <li className="flex items-start gap-2">
                <span className="font-bold">✓</span>
                <span>User Profile Verification & Verification Badge</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">✓</span>
                <span>Full Profile Customization & Visibility</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">✓</span>
                <span>Unlimited Business Chats</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">✓</span>
                <span>100 Monthly Boosts</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">✓</span>
                <span>FREE FltrLite Plus Upgrade</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white font-bold text-lg rounded-xl hover:shadow-2xl transition-all transform hover:scale-105 border-4 border-blue-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
